// src/components/teacher/TeacherTimetable.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders } from "../../utils/api";
import { CalendarIcon, ClockIcon } from "../shared/icons/Icons";
import "../superadmin/teachers/TimetableCampusPage.css";

const PERIODS_KEY = "als_timetable_periods";
const DAYS_KEY = "als_timetable_days";

const DEFAULT_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

function defaultPeriods() {
  return [
    { id: "p1", type: "period", label: "Period 1", start_time: "08:00", end_time: "08:45" },
    { id: "p2", type: "period", label: "Period 2", start_time: "08:45", end_time: "09:30" },
    { id: "p3", type: "period", label: "Period 3", start_time: "09:30", end_time: "10:15" },
    { id: "p4", type: "period", label: "Period 4", start_time: "10:15", end_time: "11:00" },
    { id: "p5", type: "period", label: "Period 5", start_time: "11:00", end_time: "11:45" },
    { id: "break", type: "break", label: "Break", start_time: "11:45", end_time: "12:00" },
    { id: "p6", type: "period", label: "Period 6", start_time: "12:00", end_time: "12:45" },
    { id: "p7", type: "period", label: "Period 7", start_time: "12:45", end_time: "13:30" },
    { id: "p8", type: "period", label: "Period 8", start_time: "13:30", end_time: "14:15" },
  ];
}

function loadFromStorage(key, campusId, fallback) {
  if (!campusId) return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.[String(campusId)]) ? parsed[String(campusId)] : fallback;
  } catch (err) {
    console.error(`Failed to parse localStorage key "${key}"`, err);
    return fallback;
  }
}

function normalizeTime(value) {
  if (!value) return "";
  const text = String(value);
  if (!text.includes(":")) return "";
  const [hours = "00", minutes = "00"] = text.split(":");
  return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
}

function formatTime12(value) {
  const time = normalizeTime(value);
  if (!time) return "";
  const [hStr, mStr] = time.split(":");
  let hour = parseInt(hStr, 10);
  if (Number.isNaN(hour)) return "";
  const ampm = hour >= 12 ? "PM" : "AM";
  hour %= 12;
  if (hour === 0) hour = 12;
  return `${hour}:${mStr} ${ampm}`;
}

function toMinutes(value) {
  const time = normalizeTime(value);
  if (!time) return 0;
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function formatRange(periodOrEntry) {
  if (!periodOrEntry?.start_time || !periodOrEntry?.end_time) return "";
  return `${formatTime12(periodOrEntry.start_time)} - ${formatTime12(periodOrEntry.end_time)}`;
}

function getClassLabel(entry) {
  const name = entry.class_name || `Class ${entry.class_id}`;
  return entry.section ? `${name} - ${entry.section}` : name;
}

function classKey(entry) {
  return `${entry.class_id}::${entry.section || ""}`;
}

function TeacherTimetable({ teacherData, loading: parentLoading }) {
  const navigate = useNavigate();
  const [timetable, setTimetable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClassKey, setSelectedClassKey] = useState("all");
  const [viewMode, setViewMode] = useState("grid");

  useEffect(() => {
    const fetchTimetable = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/teachers/me/timetable`, {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch timetable");
        }

        const data = await response.json();
        setTimetable(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch timetable:", err);
        setError("Could not load your timetable.");
      } finally {
        setLoading(false);
      }
    };

    fetchTimetable();
  }, []);

  const campusId = useMemo(() => {
    return (
      teacherData?.campus_id ||
      timetable.find((entry) => entry.campus_id)?.campus_id ||
      localStorage.getItem("als_teacher_campus_id") ||
      ""
    );
  }, [teacherData, timetable]);

  const today = new Date().toLocaleDateString("en-US", { weekday: "long" });

  const classOptions = useMemo(() => {
    const map = new Map();
    timetable.forEach((entry) => {
      if (entry.class_id == null) return;
      const key = classKey(entry);
      if (!map.has(key)) {
        map.set(key, {
          key,
          class_id: entry.class_id,
          section: entry.section || "",
          label: getClassLabel(entry),
        });
      }
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [timetable]);

  const filteredTimetable = useMemo(() => {
    if (selectedClassKey === "all") return timetable;
    return timetable.filter((entry) => classKey(entry) === selectedClassKey);
  }, [timetable, selectedClassKey]);

  const days = useMemo(() => {
    const savedDays = loadFromStorage(DAYS_KEY, campusId, null);
    const entryDays = [
      ...new Set(filteredTimetable.map((entry) => entry.day_of_week).filter(Boolean)),
    ];
    const baseDays = Array.isArray(savedDays) ? savedDays : DEFAULT_DAYS.slice(0, 5);
    const merged = [...new Set([...baseDays, ...entryDays])];
    return merged.sort((a, b) => {
      const aIndex = DEFAULT_DAYS.indexOf(a);
      const bIndex = DEFAULT_DAYS.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  }, [campusId, filteredTimetable]);

  const periods = useMemo(() => {
    const savedPeriods = loadFromStorage(PERIODS_KEY, campusId, null);
    const periodMap = new Map(
      (Array.isArray(savedPeriods) ? savedPeriods : defaultPeriods()).map((period) => [
        period.id,
        { ...period },
      ]),
    );

    filteredTimetable.forEach((entry) => {
      if (!entry.period_number) return;
      const id = `p${entry.period_number}`;
      const existing = periodMap.get(id);
      periodMap.set(id, {
        id,
        type: "period",
        label: existing?.label || `Period ${entry.period_number}`,
        start_time: normalizeTime(entry.start_time) || existing?.start_time || "",
        end_time: normalizeTime(entry.end_time) || existing?.end_time || "",
      });
    });

    // Keep only periods that appear in the teacher's timetable (plus break if configured).
    const usedPeriodIds = new Set(
      filteredTimetable.map((entry) => `p${entry.period_number}`).filter(Boolean),
    );
    const breakPeriods = [...periodMap.values()].filter((p) => p.type === "break");
    const usedPeriods = [...periodMap.values()].filter(
      (p) => p.type === "period" && usedPeriodIds.has(p.id),
    );

    // If no slots yet, still show the campus period template so the grid is readable.
    const source =
      usedPeriods.length > 0
        ? [...usedPeriods, ...breakPeriods]
        : [...periodMap.values()];

    return source.sort((a, b) => {
      const aMin = toMinutes(a.start_time);
      const bMin = toMinutes(b.start_time);
      if (aMin !== bMin) return aMin - bMin;
      if (a.type === "break" && b.type !== "break") return 1;
      if (b.type === "break" && a.type !== "break") return -1;
      return String(a.id).localeCompare(String(b.id));
    });
  }, [campusId, filteredTimetable]);

  const periodById = useMemo(() => {
    return new Map(periods.map((period) => [period.id, period]));
  }, [periods]);

  const sortedEntries = useMemo(() => {
    return [...filteredTimetable].sort((a, b) => {
      const dayDiff = days.indexOf(a.day_of_week) - days.indexOf(b.day_of_week);
      if (dayDiff !== 0) return dayDiff;
      const periodDiff = (a.period_number || 0) - (b.period_number || 0);
      if (periodDiff !== 0) return periodDiff;
      return getClassLabel(a).localeCompare(getClassLabel(b));
    });
  }, [days, filteredTimetable]);

  const getEntries = (day, periodId) => {
    const periodNumber = parseInt(String(periodId).replace("p", ""), 10);
    return filteredTimetable.filter(
      (entry) =>
        entry.day_of_week === day &&
        Number(entry.period_number) === periodNumber,
    );
  };

  const openAttendance = (entry) => {
    navigate("/teacher/attendance", {
      state: {
        classId: entry.class_id,
        courseId: entry.course_id,
        courseName: entry.course_name,
        className: entry.class_name,
        section: entry.section,
        periodNumber: entry.period_number,
        timetableEntryId: entry.id,
      },
    });
  };

  if (parentLoading || loading) {
    return (
      <div className="tp-loading">
        <div className="tp-spinner" />
        <p>Loading your timetable...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tp-error">
        <h3>Unable to load timetable</h3>
        <p>{error}</p>
        <button className="tp-btn" type="button" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  const teacherName =
    teacherData?.name || localStorage.getItem("als_teacher_name") || "My Timetable";

  return (
    <div className="teacher-timetable tt-page">
      <div className="tp-page-header">
        <div>
          <h2>
            <CalendarIcon /> My Timetable
          </h2>
          <p>
            All periods assigned to you across {classOptions.length || 0} class
            {classOptions.length === 1 ? "" : "es"}
          </p>
        </div>
        <div className="tp-header-badges">
          <span className="tp-badge">{filteredTimetable.length} scheduled slots</span>
          <span className="tp-badge tp-badge--today">{today}</span>
        </div>
      </div>

      <div className="tt-header-actions" style={{ marginBottom: "1rem" }}>
        <button
          type="button"
          className={`tt-view-toggle ${viewMode === "grid" ? "active" : ""}`}
          onClick={() => setViewMode("grid")}
        >
          Grid
        </button>
        <button
          type="button"
          className={`tt-view-toggle ${viewMode === "list" ? "active" : ""}`}
          onClick={() => setViewMode("list")}
        >
          By Class
        </button>
      </div>

      {timetable.length > 0 ? (
        <>
          <div className="tt-controls-bar">
            <div className="tt-control-field">
              <label>Teacher</label>
              <select value={teacherName} disabled>
                <option>{teacherName}</option>
              </select>
            </div>
            <div className="tt-control-field">
              <label>Class</label>
              <select
                value={selectedClassKey}
                onChange={(e) => setSelectedClassKey(e.target.value)}
              >
                <option value="all">All classes</option>
                {classOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="tt-slots-count">
              <span className="tt-slots-icon">
                <ClockIcon />
              </span>
              {filteredTimetable.length} period
              {filteredTimetable.length !== 1 ? "s" : ""} assigned this week
            </div>
          </div>

          {viewMode === "grid" ? (
            <div className="tt-grid-wrapper">
              <table className="tt-grid">
                <thead>
                  <tr>
                    <th className="tt-col-period">Period</th>
                    {days.map((day) => (
                      <th key={day} className={day === today ? "tt-today-heading" : undefined}>
                        {day.slice(0, 3).toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {periods.map((period, rowIndex) =>
                    period.type === "break" ? (
                      <tr key={period.id} className="tt-break-row">
                        <td className="tt-period-label">
                          <div className="tt-period-name">{period.label}</div>
                          <div className="tt-period-time">{formatRange(period)}</div>
                        </td>
                        <td colSpan={days.length} className="tt-break-cell">
                          <span className="tt-break-pill">Break - {formatRange(period)}</span>
                        </td>
                      </tr>
                    ) : (
                      <tr key={period.id} className={rowIndex % 2 === 0 ? "tt-row-a" : "tt-row-b"}>
                        <td className="tt-period-label">
                          <div className="tt-period-name">{period.label}</div>
                          <div className="tt-period-time">{formatRange(period)}</div>
                        </td>
                        {days.map((day) => {
                          const entries = getEntries(day, period.id);
                          return (
                            <td
                              key={`${day}-${period.id}`}
                              className="tt-cell"
                              data-day={day.slice(0, 3).toUpperCase()}
                            >
                              {entries.length > 0 ? (
                                <div className="teacher-tt-slot-stack">
                                  {entries.map((entry) => (
                                    <button
                                      key={entry.id}
                                      type="button"
                                      className="tt-slot-filled teacher-tt-slot"
                                      onClick={() => openAttendance(entry)}
                                      title={`Take attendance for ${entry.course_name || "this class"}`}
                                    >
                                      <div className="tt-slot-course">
                                        {entry.course_name || "Course"}
                                      </div>
                                      <div className="tt-slot-teacher">{getClassLabel(entry)}</div>
                                      {entry.room ? (
                                        <div className="tt-slot-room">Room {entry.room}</div>
                                      ) : null}
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <div className="tt-slot-empty">-</div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          ) : null}

          {(viewMode === "list" || viewMode === "grid") && (
            <div className="tp-table-wrap teacher-tt-list">
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Period</th>
                    <th>Time</th>
                    <th>Class</th>
                    <th>Course</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEntries.map((entry) => {
                    const period = periodById.get(`p${entry.period_number}`);
                    return (
                      <tr key={entry.id}>
                        <td>{entry.day_of_week}</td>
                        <td>{period?.label || `Period ${entry.period_number}`}</td>
                        <td>{formatRange(entry) || formatRange(period)}</td>
                        <td>
                          <span className="tp-chip tp-chip--class">{getClassLabel(entry)}</span>
                        </td>
                        <td>{entry.course_name || "Course"}</td>
                        <td>
                          <button
                            className="tp-btn tp-btn--small"
                            type="button"
                            onClick={() => openAttendance(entry)}
                          >
                            Attendance
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div className="tp-empty">
          <CalendarIcon />
          <h3>No timetable assigned</h3>
          <p>Your admin has not scheduled any periods for you yet.</p>
        </div>
      )}
    </div>
  );
}

export default TeacherTimetable;
